import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { PrintLetterHead, useLetterHead } from '../../components/ui/PrintLetterHead';
import { useStorePurchaseOrder } from './store.queries';

const dash = (value: unknown): string => (value === null || value === undefined || value === '' ? '-' : String(value));
const PO_STATUS_LABELS: Record<string, string> = { draft: 'Draft', verified: 'Verified', approved: 'Approved', rejected: 'Rejected', cancelled: 'Cancelled', closed: 'Closed' };
const poStatusLabel = (value: unknown): string => PO_STATUS_LABELS[String(value)] ?? dash(value);
const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
const formatPrintDateTime = (value: unknown): string => {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '-';
  const parts = Object.fromEntries(dateTimeFormatter.formatToParts(date).map((part) => [part.type, part.value]));
  return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute} ${(parts.dayPeriod ?? '').toUpperCase()}`;
};
const formatMoney = (value: number): string => value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
const asRecord = (value: unknown): Record<string, unknown> | null => (value && typeof value === 'object' ? value as Record<string, unknown> : null);

export const StorePurchaseOrderPrintPage = () => {
  const { id } = useParams();
  const letterHead = useLetterHead();
  const query = useStorePurchaseOrder(id ?? '');

  useEffect(() => {
    if (letterHead.isReady && query.data && query.data.status === 'approved') window.setTimeout(() => window.print(), 300);
  }, [letterHead.isReady, query.data]);

  if (query.isLoading || !letterHead.isReady) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState title="Purchase order not found" message="The purchase order could not be loaded." />;
  if (query.data.status !== 'approved') return <ErrorState title="Not available for print" message="Only an approved purchase order can be printed." />;

  const row = query.data;
  const vendor = asRecord(row.vendor);
  const generatedBy = asRecord(row.generatedBy);
  const verifiedBy = asRecord(row.verifiedBy);
  const approvedBy = asRecord(row.approvedBy);
  const requisitions = Array.isArray(row.requisitions) ? row.requisitions as Record<string, unknown>[] : [];
  const lines = Array.isArray(row.lines) ? row.lines as Record<string, unknown>[] : [];

  const lineRows = lines.map((line, index) => {
    const itemRef = asRecord(line.item);
    const itemUnit = asRecord(itemRef?.unit);
    const itemSubUnit = asRecord(itemRef?.subUnit);
    const unit = itemUnit ?? asRecord(line.unit);
    const subUnit = itemSubUnit ?? asRecord(line.subUnit);
    const unitName = String(itemUnit?.name ?? unit?.name ?? line.unitName ?? itemRef?.unitName ?? '').trim();
    const subUnitName = String(itemSubUnit?.name ?? subUnit?.name ?? line.subUnitName ?? itemRef?.subUnitName ?? '').trim();
    const subUnitsPerUnit = Number(itemRef?.subUnitsPerUnit ?? line.subUnitsPerUnit ?? 1) || 1;
    const unitQuantity = Number(line.unitQuantity ?? 0);
    const subUnitQuantity = Number(line.subUnitQuantity ?? 0);
    const quantity = Number(line.quantity ?? 0);
    const rate = Number(line.rate ?? 0);
    const gross = (quantity / subUnitsPerUnit) * rate;
    const discountValue = line.discountType === 'amount'
      ? Math.min(gross, Number(line.discountAmount ?? 0))
      : gross * Number(line.discountPercent ?? 0) / 100;
    const taxPercent = Number(line.taxPercent ?? 0);
    const taxAmount = Number(((gross - discountValue) * taxPercent / 100).toFixed(2));
    const amount = Number(line.amount ?? 0);
    const discountPercent = Number(line.discountPercent ?? 0);
    return { key: dash(line._id ?? index), index, itemName: dash(line.itemName), unitName, subUnitName, subUnitsPerUnit, unitQuantity, subUnitQuantity, quantity, rate, discountValue, discountPercent, taxPercent, taxAmount, amount };
  });
  const grandTotal = lineRows.reduce((sum, line) => sum + line.amount, 0);
  const vendorAddress = [vendor?.address, vendor?.pinCode].filter(Boolean).join(', ');
  const vendorContacts = [vendor?.phone && `Mobile:${vendor.phone}`, vendor?.email && `Email:${vendor.email}`, vendor?.gstin && `GSTIN:${vendor.gstin}`, vendor?.panNumber && `PAN:${vendor.panNumber}`].filter(Boolean).join(', ');

  return (
    <div className="print-requisition-page">
      <div className="print-requisition-toolbar no-print">
        <button type="button" onClick={() => window.print()}>Print</button>
        <button type="button" className="danger" onClick={() => window.close()}>Close</button>
      </div>

      <PrintLetterHead imageUrl={letterHead.imageUrl} />
      <div className="print-requisition-header">
        <h2>Store Purchase Order</h2>
      </div>

      <table className="print-requisition-header-table"><tbody>
        <tr>
          <td><strong>Purchase Order No :</strong> {dash(row.purchaseOrderNumber)}</td>
          <td><strong>Purchase Order Date :</strong> {formatPrintDateTime(row.orderDate)}</td>
          <td><strong>Generated By :</strong> {dash(generatedBy?.name)}</td>
        </tr>
        <tr>
          <td><strong>Verified By :</strong> {dash(verifiedBy?.name)}</td>
          <td><strong>Approved By :</strong> {dash(approvedBy?.name)}</td>
          <td><strong>Requisition No :</strong> {requisitions.map((entry) => dash(entry.requisitionNumber)).join(', ') || '-'}</td>
        </tr>
        <tr>
          <td><strong>Vendor Name :</strong> <span className="print-requisition-link">{dash(vendor?.vendorName)}</span></td>
          <td colSpan={1}><strong>Vendor Address :</strong> {dash([vendorAddress, vendorContacts].filter(Boolean).join(' '))}</td>
          <td><strong>Status :</strong> <span className="print-requisition-status">{poStatusLabel(row.status)}</span></td>
        </tr>
        <tr><td colSpan={3}><strong>Note :</strong> {row.note ? dash(row.note) : ''}</td></tr>
      </tbody></table>

      <table className="print-requisition-items print-requisition-requisitions">
        <thead><tr><th>#</th><th>Requisition No</th><th>Requisition Date</th><th>Department</th><th>Generated By</th></tr></thead>
        <tbody>{requisitions.map((entry, index) => {
          const department = asRecord(entry.department);
          const requisitionGeneratedBy = asRecord(entry.generatedBy);
          return <tr key={dash(entry._id ?? index)}><td>{index + 1}</td><td>{dash(entry.requisitionNumber)}</td><td>{formatPrintDateTime(entry.requisitionDate)}</td><td>{dash(department?.name ?? department?.departmentName)}</td><td>{dash(requisitionGeneratedBy?.name)}</td></tr>;
        })}</tbody>
      </table>

      <table className="print-requisition-items">
        <thead><tr><th>#</th><th>Item Name</th><th>Unit / Sub Unit</th><th>Rate / Unit</th><th>GST</th><th>Discount</th><th>Total</th></tr></thead>
        <tbody>
          {lineRows.map((line) => (
            <tr key={line.key}>
              <td>{line.index + 1}</td>
              <td>{line.itemName}</td>
              <td>
                {line.quantity}{line.subUnitName || line.unitName ? ` ${line.subUnitName || line.unitName}` : ''}
                {line.unitName && line.subUnitName ? <small>(1 {line.unitName} = {line.subUnitsPerUnit} {line.subUnitName})</small> : null}
              </td>
              <td>₹{formatMoney(line.rate)}</td>
              <td>GST: ({line.taxPercent.toFixed(2)}) %</td>
              <td>{line.discountPercent.toFixed(2)} %</td>
              <td>{formatMoney(line.amount)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr><th colSpan={6} className="print-requisition-total-label">Grand Total</th><th>{formatMoney(grandTotal)}</th></tr></tfoot>
      </table>

      <section className="print-purchase-order-terms">
        <h3>Terms &amp; Conditions:</h3>
        <p>{row.note ? dash(row.note) : ''}</p>
      </section>
      <div className="print-purchase-order-signatures">
        <div><strong>PREPARED BY:</strong> {dash(generatedBy?.name)}<br />Date: {formatPrintDateTime(new Date())}</div>
        <div><strong>Signature with date in purchase order</strong></div>
      </div>
    </div>
  );
};
